import React, { createContext, useContext } from "react";

import { ChipGroup } from "shared/ui/filters/ChipGroup";
import { DateRangeField } from "shared/ui/filters/DateRangeField";
import { FilterActions } from "shared/ui/filters/FilterActions";
import {
  FilterDivider,
  FilterLabel,
  FilterPanel,
  FilterRow,
} from "shared/ui/filters/FilterLayout";
import { KeywordInput } from "shared/ui/filters/KeywordInput";
import { SelectDropdown } from "shared/ui/filters/SelectDropdown";
import { useSearchFilters } from "shared/ui/filters/useSearchFilters";

const SearchPanelContext = createContext(null);
const useSearchPanel = () => useContext(SearchPanelContext);

/**
 * 조건 검색 행들. 페이지는 필요한 행만 골라 선언하고, 상태/규칙은 SearchPanel이 소유한다.
 */
function Keyword({ label = "조건 검색", dropdown, placeholder = "검색어 입력" }) {
  const sf = useSearchPanel();
  return (
    <FilterRow>
      <FilterLabel>{label}</FilterLabel>
      {dropdown ? (
        <SelectDropdown
          options={dropdown}
          value={sf.condition}
          onChange={sf.setCondition}
        />
      ) : null}
      <KeywordInput
        value={sf.keyword}
        onChange={(e) => sf.setKeyword(e.target.value)}
        placeholder={placeholder}
      />
    </FilterRow>
  );
}

function Chips({ name, label, options, deselectTo = null }) {
  const sf = useSearchPanel();
  return (
    <FilterRow>
      <FilterLabel>{label}</FilterLabel>
      <ChipGroup
        options={options}
        value={sf.chips[name]}
        onChange={(value) => sf.setChip(name, value ?? deselectTo)}
        deselectTo={deselectTo}
      />
    </FilterRow>
  );
}

function QuickRange({ label = "게시 기간", options, dropdown }) {
  const sf = useSearchPanel();
  return (
    <FilterRow>
      <FilterLabel>{label}</FilterLabel>
      {dropdown ? (
        <SelectDropdown
          options={dropdown}
          value={sf.rangeField}
          onChange={sf.setRangeField}
        />
      ) : null}
      <ChipGroup
        options={options}
        value={sf.quickRange}
        onChange={sf.setQuickRange}
        deselectTo={null}
      />
    </FilterRow>
  );
}

function DateRange({ label = "검색 범위" }) {
  const sf = useSearchPanel();
  return (
    <FilterRow>
      <FilterLabel>{label}</FilterLabel>
      <DateRangeField
        startDate={sf.startDate}
        endDate={sf.endDate}
        onChangeStart={sf.onChangeStart}
        onChangeEnd={sf.onChangeEnd}
      />
    </FilterRow>
  );
}

// 선언된 자식(행)들로부터 상태 초기화에 필요한 구성 정보를 추출한다.
const toFieldConfig = (child) => {
  if (!React.isValidElement(child)) return null;
  switch (child.type) {
    case Keyword:
      return { kind: "keyword", conditionOptions: child.props.dropdown };
    case Chips:
      return {
        kind: "chips",
        name: child.props.name,
        deselectTo: child.props.deselectTo ?? null,
      };
    case QuickRange:
      return {
        kind: "quickRange",
        options: child.props.options,
        dropdown: child.props.dropdown,
      };
    case DateRange:
      return { kind: "dateRange" };
    default:
      return null;
  }
};

/**
 * 조건 검색 패널 공용 컴포넌트.
 *
 * 페이지는 필요한 행만 자식으로 선언하고(아래 예), 상태·검증·검색활성·초기화는
 * 이 컴포넌트가 모두 소유한다. onSearch에는 정규화된 filters 객체가 전달된다.
 *
 *   <SearchPanel onSearch={fn} onReset={fn}>
 *     <SearchPanel.Keyword label="조건 검색" dropdown={["제목만", ...]} />
 *     <SearchPanel.Chips name="status" label="구분" options={...} deselectTo="all" />
 *     <SearchPanel.QuickRange options={QUICK_RANGE_OPTIONS} />
 *     <SearchPanel.DateRange />
 *   </SearchPanel>
 */
export function SearchPanel({ children, onSearch, onReset }) {
  const fields = React.Children.toArray(children)
    .map(toFieldConfig)
    .filter(Boolean);
  const sf = useSearchFilters(fields);

  return (
    <SearchPanelContext.Provider value={sf}>
      <FilterPanel>
        {children}
        <FilterDivider />
        <FilterActions
          onSearch={() => sf.submit(onSearch)}
          onReset={() => sf.reset(onReset)}
          searchActive={sf.searchActive}
        />
      </FilterPanel>
    </SearchPanelContext.Provider>
  );
}

SearchPanel.Keyword = Keyword;
SearchPanel.Chips = Chips;
SearchPanel.QuickRange = QuickRange;
SearchPanel.DateRange = DateRange;

export default SearchPanel;
